import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Hash, FileText, Bookmark, Pencil, Trash2, Loader2, Copy,
} from 'lucide-react';

const API = '/api';

const TYPE_CONFIG = {
  hashtag: { label: 'Hashtag', icon: Hash, color: 'bg-blue-100 text-blue-700' },
  caption: { label: 'Caption', icon: FileText, color: 'bg-emerald-100 text-emerald-700' },
  blog: { label: 'Blog', icon: Bookmark, color: 'bg-violet-100 text-violet-700' },
};

export default function TemplatesManager() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', content: '', template_type: 'hashtag' });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.template_type = filterType;
      const { data } = await axios.get(`${API}/social/templates`, { params, withCredentials: true });
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', content: '', template_type: 'hashtag' });
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditing(tmpl);
    setForm({ name: tmpl.name, content: tmpl.content, template_type: tmpl.template_type });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/social/templates/${editing.template_id}`, form, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/social/templates`, form, { withCredentials: true });
        toast.success('Template created');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (e) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tmpl) => {
    if (!window.confirm(`Delete template "${tmpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/social/templates/${tmpl.template_id}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) {
      toast.error('Failed to delete template');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filtered = templates;

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4 md:p-6" data-testid="templates-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/social')} className="mb-3 text-slate-500">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Templates</h1>
            <p className="text-sm text-slate-500 mt-1">Save reusable hashtags, captions, and blog snippets</p>
          </div>
          <Button onClick={openCreate} data-testid="create-template-btn">
            <Plus className="h-4 w-4 mr-2" /> New Template
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'hashtag', 'caption', 'blog'].map(t => (
          <Button
            key={t}
            variant={filterType === t ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType(t)}
            data-testid={`filter-${t}`}
          >
            {t === 'all' ? 'All' : TYPE_CONFIG[t]?.label || t}
          </Button>
        ))}
      </div>

      {/* Template List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Bookmark className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 font-medium">No templates yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first template to save time</p>
            <Button className="mt-4" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" /> Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(tmpl => {
            const cfg = TYPE_CONFIG[tmpl.template_type] || TYPE_CONFIG.hashtag;
            const Icon = cfg.icon;
            return (
              <Card key={tmpl.template_id} className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`template-${tmpl.template_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-sm text-slate-800">{tmpl.name}</span>
                        <Badge className={`${cfg.color} text-[10px]`}>{cfg.label}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-3">{tmpl.content}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(tmpl.content)} title="Copy">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(tmpl)} title="Edit" data-testid={`edit-${tmpl.template_id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => handleDelete(tmpl)} title="Delete" data-testid={`delete-${tmpl.template_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.template_type} onValueChange={v => setForm(p => ({ ...p, template_type: v }))}>
                <SelectTrigger data-testid="template-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hashtag">Hashtag</SelectItem>
                  <SelectItem value="caption">Caption</SelectItem>
                  <SelectItem value="blog">Blog Snippet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Product Launch Hashtags"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={form.content}
                onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                placeholder={form.template_type === 'hashtag' ? '#PowerGen #DieselGenerator #ShardaMotor...' : 'Enter your template text...'}
                rows={5}
                data-testid="template-content-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
